import {
  IconButton,
  Popover,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import { Stack } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import { useMemo, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { exportKpiCsv } from "../../../../helpers/exportKpiCsv";

type ViewMode = "Month" | "Quarter";

interface KpiRRfilterProps {
  onDateChange?: (startDate: string, endDate: string) => void;
}

// ─── Date helpers ──────────────────────────────────────────────────────────────

/** Formats a Date to YYYY-MM-DD */
function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Returns today minus N days at midnight local time */
function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Last day of a given month (0-indexed) */
function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

/** Quarter index (0–3) for a given month (0-indexed) */
function quarterOf(month: number): number {
  return Math.floor(month / 3);
}

/** First month (0-indexed) of a quarter (0–3) */
function firstMonthOfQuarter(q: number): number {
  return q * 3;
}

/** Last month (0-indexed) of a quarter (0–3) */
function lastMonthOfQuarter(q: number): number {
  return q * 3 + 2;
}

// ─── List generation ──────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const QUARTER_MONTH_RANGES = ["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"];

/** Generates a descending list of month option labels (24 months back) */
function generateMonthOptions(): string[] {
  const now = new Date();
  const options: string[] = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push(`${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`);
  }
  return options;
}

/** Generates a descending list of quarter option labels (8 quarters back) */
function generateQuarterOptions(): string[] {
  const now = new Date();
  const options: string[] = [];
  let year = now.getFullYear();
  let q = quarterOf(now.getMonth());
  for (let i = 0; i < 8; i++) {
    options.push(`Q${q + 1} ${year} (${QUARTER_MONTH_RANGES[q]})`);
    q--;
    if (q < 0) {
      q = 3;
      year--;
    }
  }
  return options;
}

// ─── Date computation ─────────────────────────────────────────────────────────

function computeDatesForMonth(label: string): { start: string; end: string } {
  const [monthName, yearStr] = label.split(" ");
  const year = parseInt(yearStr, 10);
  const month = MONTH_NAMES.indexOf(monthName);
  const start = new Date(year, month, 1);

  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  const end = isCurrentMonth ? daysAgo(2) : lastDayOfMonth(year, month);
  return { start: formatDate(start), end: formatDate(end) };
}

function computeDatesForQuarter(label: string): { start: string; end: string } {
  // e.g. "Q1 2026 (Jan–Mar)"
  const match = label.match(/Q(\d)\s+(\d{4})/);
  if (!match)
    return { start: formatDate(new Date()), end: formatDate(new Date()) };
  const q = parseInt(match[1], 10) - 1; // 0-indexed
  const year = parseInt(match[2], 10);

  const start = new Date(year, firstMonthOfQuarter(q), 1);

  const now = new Date();
  const currentQ = quarterOf(now.getMonth());
  const isCurrentQuarter = year === now.getFullYear() && q === currentQ;

  const end = isCurrentQuarter
    ? daysAgo(2)
    : lastDayOfMonth(year, lastMonthOfQuarter(q));

  return { start: formatDate(start), end: formatDate(end) };
}

function formatButtonLabel(
  period: string,
  viewMode: ViewMode,
  start: string,
  end: string,
) {
  if (!start || !end) return period;

  const formatDateDDMM = (dateStr: string) => {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`;
    }
    return dateStr;
  };

  const startPart = formatDateDDMM(start);
  const endPart = formatDateDDMM(end);

  if (viewMode === "Month") {
    // period is "April 2025" -> "Apr, 2025 (01/04-30/04)"
    const [monthFull, year] = period.split(" ");
    const monthShort = monthFull.substring(0, 3);
    return `${monthShort}, ${year} (${startPart}-${endPart})`;
  } else {
    // period is "Q1 2026 (Jan–Mar)" -> "Q1, 2026 (01/01-31/03)"
    const match = period.match(/(Q\d)\s+(\d{4})/);
    if (match) {
      return `${match[1]}, ${match[2]} (${startPart}-${endPart})`;
    }
    return period;
  }
}

function formatOptionLabel(option: string, viewMode: ViewMode) {
  if (viewMode === "Month") {
    const [monthFull, year] = option.split(" ");
    return `${monthFull.substring(0, 3)} ${year}`;
  } else {
    return option.split(" (")[0];
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const KpiRRfilter = ({ onDateChange }: KpiRRfilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { targetVsActualPR } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);

  const viewMode = (searchParams.get("view") as ViewMode) || "Month";
  const selectedPeriod =
    searchParams.get("period") ||
    (viewMode === "Month" ? monthOptions[0] : quarterOptions[0]);

  // Use refs to hold the *last reported* dates so we don't spam onDateChange
  const lastReportedDates = useRef<{ start: string; end: string }>({
    start: "",
    end: "",
  });

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const [popoverViewMode, setPopoverViewMode] = useState<ViewMode>(viewMode);

  useEffect(() => {
    if (open) {
      setPopoverViewMode(viewMode);
    }
  }, [open, viewMode]);

  const options = popoverViewMode === "Month" ? monthOptions : quarterOptions;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    // 1. Calculate the *expected* state based on current viewMode & period
    const { start: expectedStart, end: expectedEnd } =
      viewMode === "Month"
        ? computeDatesForMonth(selectedPeriod)
        : computeDatesForQuarter(selectedPeriod);

    // 2. Read the *actual* state from the URL
    const urlStart = searchParams.get("start_date");
    const urlEnd = searchParams.get("end_date");
    const urlView = searchParams.get("view");
    const urlPeriod = searchParams.get("period");

    // 3. If the URL is missing any required parameters OR the dates don't match the period, update the URL.
    if (
      urlView !== viewMode ||
      urlPeriod !== selectedPeriod ||
      urlStart !== expectedStart ||
      urlEnd !== expectedEnd
    ) {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set("view", viewMode);
      currentParams.set("period", selectedPeriod);
      currentParams.set("start_date", expectedStart);
      currentParams.set("end_date", expectedEnd);
      // 'replace: true' prevents filling the back stack with redirect frames
      setSearchParams(currentParams, { replace: true });
      return; // Exit early, the next render will have the correct searchParams
    }

    // 4. If URL is correct, ensure we broadcast `onDateChange` exactly once for these dates.
    if (expectedStart && expectedEnd) {
      if (
        lastReportedDates.current.start !== expectedStart ||
        lastReportedDates.current.end !== expectedEnd
      ) {
        lastReportedDates.current = { start: expectedStart, end: expectedEnd };
        onDateChange?.(expectedStart, expectedEnd);
      }
    }
  }, [viewMode, selectedPeriod, searchParams, setSearchParams, onDateChange]);

  const handleTabChange = (_: React.SyntheticEvent, newMode: ViewMode) => {
    if (newMode && newMode !== popoverViewMode) {
      setPopoverViewMode(newMode);
    }
  };

  const handleOptionSelect = (newValue: string) => {
    if (newValue !== selectedPeriod || popoverViewMode !== viewMode) {
      const { start, end } =
        popoverViewMode === "Month"
          ? computeDatesForMonth(newValue)
          : computeDatesForQuarter(newValue);

      const currentParams = new URLSearchParams(searchParams);
      currentParams.set("view", popoverViewMode);
      currentParams.set("period", newValue);
      currentParams.set("start_date", start);
      currentParams.set("end_date", end);
      setSearchParams(currentParams);
    }
    handleClose();
  };

  const { start: expectedStart, end: expectedEnd } =
    viewMode === "Month"
      ? computeDatesForMonth(selectedPeriod)
      : computeDatesForQuarter(selectedPeriod);

  const handleExport = () => {
    exportKpiCsv(targetVsActualPR, viewMode, selectedPeriod);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.xs.$value,
          mb: spacing.Spacing.md.$value,
        }}
      >
        <Button
          onClick={handleClick}
          variant="outlined"
          endIcon={
            <KeyboardArrowDownIcon
              sx={{ color: colors.GrayLightMode[600].$value }}
            />
          }
          data-tracker="644"
          sx={{
            borderColor: colors.GrayLightMode[300].$value,
            color: colors.GrayLightMode[800].$value,
            borderRadius: spacing.Radius.md.$value,
            textTransform: "none",
            fontWeight: typography.Font_weight.Semibold.$value,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            height: spacing.Spacing["4xl"].$value,
            px: 1.5,
            "&:hover": {
              borderColor: colors.GrayLightMode[400].$value,
              backgroundColor: "transparent",
            },
          }}
        >
          {formatButtonLabel(
            selectedPeriod,
            viewMode,
            expectedStart,
            expectedEnd,
          )}
        </Button>

        <IconButton color="secondary" size="large" onClick={handleExport}>
          <FileDownloadIcon
            sx={{ color: colors.GrayLightMode[700].$value }}
            data-tracker="645"
          />
        </IconButton>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 340,
            borderRadius: spacing.Radius.md.$value,
            boxShadow: `0px 4px 12px rgba(0, 0, 0, 0.1)`,
            border: `1px solid ${colors.GrayLightMode[200].$value}`,
          },
        }}
      >
        <Tabs
          value={popoverViewMode}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            style: { display: "none" },
          }}
          sx={{
            minHeight: "48px",
            borderBottom: `1px solid ${colors.GrayLightMode[200].$value}`,
            "& .MuiTab-root": {
              minHeight: "48px",
              textTransform: "none",
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.sm.$value,
              color: colors.GrayLightMode[500].$value,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
            },
            "& .Mui-selected": {
              color: `${colors.GrayLightMode[800].$value} !important`,
            },
          }}
        >
          <Tab
            value="Month"
            disableRipple
            label={
              <span style={{ display: "flex", alignItems: "center" }}>
                Month{" "}
                {popoverViewMode === "Month" && (
                  <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
                )}
              </span>
            }
          />
          <Tab
            value="Quarter"
            disableRipple
            label={
              <span style={{ display: "flex", alignItems: "center" }}>
                Quarter{" "}
                {popoverViewMode === "Quarter" && (
                  <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
                )}
              </span>
            }
          />
        </Tabs>

        <MenuList sx={{ maxHeight: 300, overflow: "auto", p: 0 }}>
          {options.map((option) => {
            const isSelected =
              option === selectedPeriod && popoverViewMode === viewMode;
            return (
              <MenuItem
                key={option}
                onClick={() => handleOptionSelect(option)}
                sx={{
                  py: 1.5,
                  px: 2,
                  backgroundColor: isSelected
                    ? colors.Brand[50].$value
                    : "transparent",
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isSelected && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: colors.GrayLightMode[800].$value }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={formatOptionLabel(option, popoverViewMode)}
                  primaryTypographyProps={{
                    fontSize: typography.Size.Text.sm.$value,
                    fontWeight: isSelected
                      ? typography.Font_weight.Medium.$value
                      : typography.Font_weight.Regular.$value,
                    color: colors.GrayLightMode[800].$value,
                  }}
                />
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
};

export default KpiRRfilter;
